'use client';

import posthog from 'posthog-js';

/**
 * PostHog Analytics Client Initialization
 *
 * Story 6.6: A/B Variant Refinement & Admin Analytics
 *
 * Initializes PostHog with:
 * - Feature flags for A/B testing
 * - Session replay with privacy masking
 * - Manual pageview tracking (Next.js App Router compatible)
 */

let isInitialized = false;

/**
 * Initialize PostHog client
 * Safe to call multiple times - will only initialize once
 */
export function initPostHog(): typeof posthog | null {
  // Only initialize in browser
  if (typeof window === 'undefined') {
    return null;
  }

  // Only initialize once
  if (isInitialized) {
    return posthog;
  }

  // Require API key
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!apiKey) {
    console.warn('[PostHog] NEXT_PUBLIC_POSTHOG_KEY not set - analytics disabled');
    return null;
  }

  posthog.init(apiKey, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://ext.boty.bot',
    defaults: '2026-01-30',

    // Identified profiles only (GDPR-friendly)
    person_profiles: 'identified_only',

    // Manual pageview tracking for Next.js App Router
    capture_pageview: false,
    capture_pageleave: true,

    // Session replay configuration
    disable_session_recording: false,
    session_recording: {
      // Inputs are not sensitive - don't mask by default
      maskAllInputs: false,
      // Mask sensitive elements explicitly when needed
      maskTextSelector: '[data-ph-mask]',
      // Block recording of sensitive areas
      blockSelector: '[data-ph-block]',
    },

    // Load feature flags on init
    bootstrap: {
      featureFlags: {},
    },

    // Persistence
    persistence: 'localStorage+cookie',
    // Error tracking — autocapture exceptions (Story 16.2)
    capture_exceptions: true,

    // Debugging (only in development)
    loaded: (posthogInstance) => {
      if (process.env.NODE_ENV === 'development') {
        // Enable debug mode in dev
        posthogInstance.debug();
      }
    },
  });

  isInitialized = true;
  return posthog;
}

/**
 * Get the PostHog instance
 * Returns null if not initialized
 */
export function getPostHog(): typeof posthog | null {
  if (!isInitialized) {
    return null;
  }
  return posthog;
}

/**
 * Reset PostHog state (useful for logout)
 */
export function resetPostHog(): void {
  if (isInitialized) {
    posthog.reset();
  }
}

/**
 * Identify a user with PostHog
 * Call this after login/signup
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>
): void {
  if (!isInitialized) {
    return;
  }
  posthog.identify(userId, properties);
}

// Re-export posthog for direct access when needed
export { posthog };
